import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

const variantStyles = {
  primary:
    "bg-primary text-on-primary font-semibold uppercase tracking-widest hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  secondary:
    "border border-outline-variant bg-surface-container text-on-surface font-semibold hover:border-primary/40",
  outline:
    "border border-outline-variant text-on-surface font-semibold hover:border-primary/40",
  "outline-primary":
    "border border-outline-variant text-primary font-semibold hover:bg-primary/10 hover:border-primary/40",
  danger:
    "border border-error/50 text-error font-semibold uppercase tracking-widest hover:bg-error/10",
  accent:
    "border border-tertiary/40 text-tertiary font-semibold uppercase tracking-widest hover:bg-tertiary/10",
  ghost:
    "text-on-surface-variant hover:text-primary hover:bg-surface-container-high/50",
  link: "text-primary hover:underline font-semibold",
  "link-muted":
    "text-[10px] uppercase tracking-widest text-primary hover:underline font-semibold",
  unstyled: "",
} as const;

const sizeStyles = {
  sm: "px-3 py-1.5 text-body-sm gap-1.5",
  md: "px-4 py-2 text-body-sm gap-2",
  lg: "px-6 py-2 text-body-sm gap-2",
  icon: "p-1 gap-0",
  "icon-sm": "p-0.5 gap-0",
} as const;

export type ButtonVariant = keyof typeof variantStyles;
export type ButtonSize = keyof typeof sizeStyles;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const linkVariants = new Set<ButtonVariant>(["link", "link-muted", "unstyled"]);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "outline",
      size = "md",
      type = "button",
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex items-center justify-center rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        !linkVariants.has(variant) && sizeStyles[size],
        variantStyles[variant],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";
